import type { PropsWithChildren } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_CATALOGUE_DESIGN,
  getCatalogueDesignClass,
  type CatalogueDesignKey,
  useOptionalCatalogueAppearance,
} from '@eshop/designs';

const BASE_STYLE_LOADER = () => import('@styles/catalogue/base.css');

const DESIGN_STYLE_LOADERS: Partial<Record<CatalogueDesignKey, () => Promise<unknown>>> = {
  // moderno: () => import('@styles/catalogue/moderno.css'),
  minimalista: () => import('@styles/catalogue/minimalista.css'),
  // clean: () => import('@styles/catalogue/clean.css'),
  'soft-commerce': () => import('@styles/catalogue/soft-commerce.css'),
};

export interface ThemeLoaderProps {
  design?: CatalogueDesignKey;
  fallbackDesign?: CatalogueDesignKey;
}

export interface ThemeAssets {
  /** Diseño solicitado por la UI (puede no coincidir con el cargado si se aplica fallback). */
  design: CatalogueDesignKey;
  /** Diseño que tiene los estilos efectivamente cargados. */
  resolvedDesign: CatalogueDesignKey;
  /** Clase de ayuda para componer selectores dependientes del diseño. */
  designClassName: string;
  /** Indica si se está cargando una hoja de estilos nueva. */
  isLoading: boolean;
  /** Señala que tanto la hoja base como la específica ya están disponibles. */
  isReady: boolean;
}

const defaultThemeAssets: ThemeAssets = {
  design: DEFAULT_CATALOGUE_DESIGN,
  resolvedDesign: DEFAULT_CATALOGUE_DESIGN,
  designClassName: getCatalogueDesignClass(DEFAULT_CATALOGUE_DESIGN),
  isLoading: false,
  isReady: false,
};

const ThemeAssetsContext = createContext<ThemeAssets>(defaultThemeAssets);

export const useThemeAssets = () => useContext(ThemeAssetsContext);

export const ThemeLoader = ({
  design: designOverride,
  fallbackDesign,
  children,
}: PropsWithChildren<ThemeLoaderProps>) => {
  const appearance = useOptionalCatalogueAppearance();
  const requestedDesign = designOverride ?? appearance?.design ?? DEFAULT_CATALOGUE_DESIGN;
  const requestedFallback = fallbackDesign ?? DEFAULT_CATALOGUE_DESIGN;

  const designPriority = useMemo(() => {
    const order: CatalogueDesignKey[] = [];
    const add = (value?: CatalogueDesignKey) => {
      if (!value) return;
      if (order.includes(value)) return;
      order.push(value);
    };
    add(requestedDesign);
    add(requestedFallback);
    add(DEFAULT_CATALOGUE_DESIGN);
    return order;
  }, [requestedDesign, requestedFallback]);

  const baseLoadedRef = useRef(false);
  const loadedDesignsRef = useRef(new Set<CatalogueDesignKey>());
  const [resolvedDesign, setResolvedDesign] = useState<CatalogueDesignKey>(
    designPriority[0] ?? DEFAULT_CATALOGUE_DESIGN,
  );
  const [isLoading, setIsLoading] = useState<boolean>(() => !baseLoadedRef.current);

  useEffect(() => {
    let cancelled = false;

    const loadBaseIfNeeded = async () => {
      if (baseLoadedRef.current) {
        return;
      }
      await BASE_STYLE_LOADER();
      baseLoadedRef.current = true;
    };

    const resolveDesignStyles = async (priority: CatalogueDesignKey[]) => {
      for (const candidate of priority) {
        const loader = DESIGN_STYLE_LOADERS[candidate];
        if (!loader) {
          continue;
        }
        if (!loadedDesignsRef.current.has(candidate)) {
          try {
            await loader();
            loadedDesignsRef.current.add(candidate);
          } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn(
                `No se pudo cargar la hoja de estilos para el diseño "${candidate}"`,
                error,
              );
            }
            continue;
          }
        }
        return candidate;
      }
      return DEFAULT_CATALOGUE_DESIGN;
    };

    const designAlreadyReady = () => {
      const candidate = designPriority.find((key) => DESIGN_STYLE_LOADERS[key]);
      if (!candidate) {
        return false;
      }
      return baseLoadedRef.current && loadedDesignsRef.current.has(candidate);
    };

    if (designAlreadyReady()) {
      setIsLoading(false);
      setResolvedDesign((prev) => {
        const candidate = designPriority.find((key) => DESIGN_STYLE_LOADERS[key]);
        return candidate ?? prev;
      });
      return;
    }

    setIsLoading(true);

    (async () => {
      try {
        await loadBaseIfNeeded();
        const loadedDesign = await resolveDesignStyles(designPriority);
        if (!cancelled) {
          setResolvedDesign(loadedDesign);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error al cargar los estilos del catálogo', error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [designPriority]);

  const assets = useMemo<ThemeAssets>(() => {
    const ready = baseLoadedRef.current && loadedDesignsRef.current.has(resolvedDesign);
    return {
      design: requestedDesign,
      resolvedDesign,
      designClassName: getCatalogueDesignClass(resolvedDesign),
      isLoading,
      isReady: ready,
    };
  }, [requestedDesign, resolvedDesign, isLoading]);

  return <ThemeAssetsContext.Provider value={assets}>{children ?? null}</ThemeAssetsContext.Provider>;
};

export default ThemeLoader;
